import React, { useState, useEffect, useCallback } from "react";
import {
  FaCalendarCheck,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaSearch,
  FaEye,
  FaCalendarAlt,
  FaExclamationCircle,
  FaMoneyBillWave,
  FaHandshake,
  FaStickyNote,
  FaThumbsUp,
  FaThumbsDown,
  FaQuestionCircle,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import {
  getCompletedAppointments,
  deleteAppointment,
} from "../utils/emailUtils";
import { logAdminActivity } from "../utils/adminSecurity";
import { ToastContainer } from "./Toast";
import "./CompletedAppointments.css";

const CompletedAppointments = () => {
  const [completedAppointments, setCompletedAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toasts, setToasts] = useState([]);

  const statusOptions = [
    { value: "all", label: "Tüm Sonuçlar", color: "#718096" },
    { value: "successful", label: "Başarılı", color: "#48bb78" },
    { value: "unsuccessful", label: "Başarısız", color: "#e53e3e" },
    { value: "considering", label: "Düşünüyor", color: "#ed8936" },
  ];

  const dateFilterOptions = [
    { value: "all", label: "Tüm Tarihler" },
    { value: "today", label: "Bugün" },
    { value: "yesterday", label: "Dün" },
    { value: "this_week", label: "Bu Hafta" },
    { value: "last_week", label: "Geçen Hafta" },
    { value: "this_month", label: "Bu Ay" },
    { value: "last_month", label: "Geçen Ay" },
  ];

  // Toast functions
  const addToast = useCallback((message, type = "success", duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const filterAppointments = useCallback(() => {
    let filtered = [...completedAppointments];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (appointment) =>
          appointment.userInfo?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userData?.firstName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userData?.lastName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userData?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          appointment.userInfo?.phone?.includes(searchTerm) ||
          appointment.userData?.phone?.includes(searchTerm)
      );
    }

    // Status filter (completion status)
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (appointment) => appointment.completionDetails?.status === statusFilter
      );
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((appointment) => {
        const completedDate =
          appointment.completedAt?.toDate?.() ||
          new Date(appointment.completedAt);
        const completedDay = new Date(
          completedDate.getFullYear(),
          completedDate.getMonth(),
          completedDate.getDate()
        );

        switch (dateFilter) {
          case "today":
            return completedDay.getTime() === today.getTime();
          case "yesterday":
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            return completedDay.getTime() === yesterday.getTime();
          case "this_week":
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return completedDay >= weekStart && completedDay <= weekEnd;
          case "last_week":
            const lastWeekStart = new Date(today);
            lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
            const lastWeekEnd = new Date(lastWeekStart);
            lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
            return completedDay >= lastWeekStart && completedDay <= lastWeekEnd;
          case "this_month":
            return (
              completedDate.getMonth() === now.getMonth() &&
              completedDate.getFullYear() === now.getFullYear()
            );
          case "last_month":
            const lastMonth = new Date(
              now.getFullYear(),
              now.getMonth() - 1,
              1
            );
            return (
              completedDate.getMonth() === lastMonth.getMonth() &&
              completedDate.getFullYear() === lastMonth.getFullYear()
            );
          default:
            return true;
        }
      });
    }

    // Sort by completion date - most recent first
    filtered.sort((a, b) => {
      const dateA = a.completedAt?.toDate?.() || new Date(a.completedAt);
      const dateB = b.completedAt?.toDate?.() || new Date(b.completedAt);
      return dateB - dateA;
    });

    setFilteredAppointments(filtered);
  }, [completedAppointments, searchTerm, statusFilter, dateFilter]);

  const loadCompletedAppointments = useCallback(async () => {
    setIsLoading(true);
    try {
      const appointmentData = await getCompletedAppointments();
      setCompletedAppointments(appointmentData);
    } catch (error) {
      console.error("Error loading completed appointments:", error);
      addToast("Tamamlanan randevular yüklenirken hata oluştu.", "error");
    }
    setIsLoading(false);
  }, [addToast]);

  useEffect(() => {
    loadCompletedAppointments();
  }, [loadCompletedAppointments]);

  useEffect(() => {
    filterAppointments();
  }, [filterAppointments]);

  const handleDeleteAppointment = async (appointmentId) => {
    if (
      !window.confirm(
        "Bu tamamlanan randevuyu silmek istediğinizden emin misiniz?"
      )
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const appointment = completedAppointments.find(
        (app) => app.id === appointmentId
      );

      await deleteAppointment(appointmentId);

      setCompletedAppointments((prev) =>
        prev.filter((appointment) => appointment.id !== appointmentId)
      );

      // Log the deletion
      logAdminActivity("completed_appointment_deletion", {
        appointmentId,
        appointmentDate: appointment?.date,
        appointmentTime: appointment?.time,
        completionStatus: appointment?.completionDetails?.status,
        studentEmail:
          appointment?.userData?.email || appointment?.userInfo?.email,
        studentName: `${
          appointment?.userData?.firstName || appointment?.userInfo?.firstName
        } ${
          appointment?.userData?.lastName || appointment?.userInfo?.lastName
        }`,
        timestamp: new Date().toISOString(),
        reason: "admin_manual_deletion",
      });

      addToast("Tamamlanan randevu başarıyla silindi.", "success");
    } catch (error) {
      console.error("Error deleting completed appointment:", error);
      addToast("Randevu silinirken hata oluştu.", "error");
    }
    setIsLoading(false);
  };

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const formatDate = (date) => {
    if (!date) return "Tarih belirtilmemiş";

    let dateObj;
    if (date.toDate && typeof date.toDate === "function") {
      dateObj = date.toDate();
    } else if (typeof date === "string") {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return "Geçersiz tarih";
    }

    if (isNaN(dateObj.getTime())) {
      return "Geçersiz tarih";
    }

    return dateObj.toLocaleDateString("tr-TR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatShortDate = (date) => {
    if (!date) return "Tarih yok";

    let dateObj;
    if (date.toDate && typeof date.toDate === "function") {
      dateObj = date.toDate();
    } else if (typeof date === "string") {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return "Geçersiz";
    }

    if (isNaN(dateObj.getTime())) {
      return "Geçersiz";
    }

    return dateObj.toLocaleDateString("tr-TR");
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.color : "#718096";
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(
      (option) => option.value === status
    );
    return statusOption ? statusOption.label : status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "successful":
        return <FaThumbsUp />;
      case "unsuccessful":
        return <FaThumbsDown />;
      case "considering":
        return <FaQuestionCircle />;
      default:
        return <FaQuestionCircle />;
    }
  };

  const getEducationLabel = (education) => {
    const educationMap = {
      "lisans-ogrenci": "Lisans Öğrencisi",
      "lisans-mezunu": "Lisans Mezunu",
      "12-sinif": "12. Sınıf",
      diger: "Diğer",
    };
    return educationMap[education] || education || "Belirtilmemiş";
  };

  const openAppointmentModal = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const closeAppointmentModal = () => {
    setSelectedAppointment(null);
    setShowModal(false);
  };

  const getCompletionStats = () => {
    const total = completedAppointments.length;
    const successful = completedAppointments.filter(
      (a) => a.completionDetails?.status === "successful"
    ).length;
    const unsuccessful = completedAppointments.filter(
      (a) => a.completionDetails?.status === "unsuccessful"
    ).length;
    const considering = completedAppointments.filter(
      (a) => a.completionDetails?.status === "considering"
    ).length;

    return { total, successful, unsuccessful, considering };
  };

  const stats = getCompletionStats();

  return (
    <div className="completed-appointments">
      <div className="tracker-header">
        <h2>
          <FaCalendarCheck className="section-icon" />
          Tamamlanan Randevular
        </h2>
        <p>Tamamlanan randevuları ve detaylarını görüntüleyin</p>
      </div>

      {/* Stats Cards */}
      <div className="completion-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <FaCalendarAlt />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Toplam Tamamlanan</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon successful">
            <FaThumbsUp />
          </div>
          <div className="stat-content">
            <h3>{stats.successful}</h3>
            <p>Başarılı</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon unsuccessful">
            <FaThumbsDown />
          </div>
          <div className="stat-content">
            <h3>{stats.unsuccessful}</h3>
            <p>Başarısız</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon considering">
            <FaQuestionCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.considering}</h3>
            <p>Düşünüyor</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="İsim, e-posta veya telefon ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>Sonuç</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Tamamlanma Tarihi</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              {dateFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Completed Appointments List */}
      <div className="appointments-section">
        <div className="section-header">
          <h3>
            <FaCalendarCheck className="section-icon" />
            Tamamlanan Randevular
          </h3>
          <span className="appointment-count">
            ({filteredAppointments.length})
          </span>
        </div>

        <div className="appointments-list">
          {isLoading ? (
            <div className="loading">Tamamlanan randevular yükleniyor...</div>
          ) : filteredAppointments.length === 0 ? (
            <div className="no-appointments">
              <FaExclamationCircle className="no-data-icon" />
              <p>
                {completedAppointments.length === 0
                  ? "Henüz tamamlanan randevu bulunmuyor"
                  : "Filtrelere uygun tamamlanan randevu bulunamadı"}
              </p>
            </div>
          ) : (
            filteredAppointments.map((appointment) => (
              <div key={appointment.id} className="appointment-card completed">
                <div className="appointment-header">
                  <div className="appointment-date">
                    <FaCalendarAlt className="date-icon" />
                    <div>
                      <div className="date-main">
                        {formatShortDate(appointment.date)}
                      </div>
                      <div className="time-main">
                        <FaClock className="time-icon" />
                        {appointment.time}
                      </div>
                    </div>
                  </div>
                  <div className="completion-info">
                    <div className="completion-date">
                      Tamamlandı: {formatShortDate(appointment.completedAt)}
                    </div>
                    <div
                      className="completion-status"
                      style={{
                        backgroundColor: getStatusColor(
                          appointment.completionDetails?.status
                        ),
                      }}
                    >
                      {getStatusIcon(appointment.completionDetails?.status)}
                      {getStatusLabel(appointment.completionDetails?.status)}
                    </div>
                  </div>
                </div>

                <div className="appointment-body">
                  <div className="user-info">
                    <div className="user-main">
                      <FaUser className="user-icon" />
                      <span className="user-name">
                        {appointment.userInfo?.firstName ||
                          appointment.userData?.firstName ||
                          "İsim"}{" "}
                        {appointment.userInfo?.lastName ||
                          appointment.userData?.lastName ||
                          "Soyisim"}
                      </span>
                    </div>
                    <div className="contact-info">
                      <div className="contact-item">
                        <FaEnvelope className="contact-icon" />
                        <span>
                          {appointment.userInfo?.email ||
                            appointment.userData?.email ||
                            "E-posta yok"}
                        </span>
                      </div>
                      <div className="contact-item">
                        <FaPhone className="contact-icon" />
                        <span>
                          {appointment.userInfo?.phone ||
                            appointment.userData?.phone ||
                            "Telefon yok"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="completion-details">
                    <div className="price-info">
                      <div className="price-item">
                        <FaMoneyBillWave className="price-icon" />
                        <span>
                          Teklif: €
                          {appointment.completionDetails?.proposedPrice || "0"}
                        </span>
                      </div>
                      <div className="price-item">
                        <FaHandshake className="price-icon" />
                        <span>
                          Anlaşma: €
                          {appointment.completionDetails?.agreedPrice || "0"}
                        </span>
                      </div>
                    </div>
                    {appointment.completionDetails?.notes && (
                      <div className="notes-info">
                        <FaStickyNote className="notes-icon" />
                        <span className="notes-preview">
                          {appointment.completionDetails.notes.length > 50
                            ? `${appointment.completionDetails.notes.substring(
                                0,
                                50
                              )}...`
                            : appointment.completionDetails.notes}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="appointment-actions">
                    <button
                      className="action-button view"
                      onClick={() => openAppointmentModal(appointment)}
                    >
                      <FaEye />
                    </button>

                    <button
                      className="action-button delete"
                      onClick={() => handleDeleteAppointment(appointment.id)}
                      disabled={isLoading}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Appointment Detail Modal */}
      {showModal && selectedAppointment && (
        <div className="modal-overlay" onClick={closeAppointmentModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tamamlanan Randevu Detayları</h3>
              <button className="close-button" onClick={closeAppointmentModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Randevu Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Randevu Tarihi</label>
                    <span>{formatDate(selectedAppointment.date)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Randevu Saati</label>
                    <span>{selectedAppointment.time}</span>
                  </div>
                  <div className="detail-item">
                    <label>Tamamlanma Tarihi</label>
                    <span>{formatDate(selectedAppointment.completedAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Sonuç</label>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(
                          selectedAppointment.completionDetails?.status
                        ),
                      }}
                    >
                      {getStatusIcon(
                        selectedAppointment.completionDetails?.status
                      )}
                      {getStatusLabel(
                        selectedAppointment.completionDetails?.status
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Kişi Bilgileri</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Ad Soyad</label>
                    <span>
                      {selectedAppointment.userInfo?.firstName ||
                        selectedAppointment.userData?.firstName ||
                        "İsim"}{" "}
                      {selectedAppointment.userInfo?.lastName ||
                        selectedAppointment.userData?.lastName ||
                        "Soyisim"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>E-posta</label>
                    <span>
                      {selectedAppointment.userInfo?.email ||
                        selectedAppointment.userData?.email ||
                        "E-posta yok"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Telefon</label>
                    <span>
                      {selectedAppointment.userInfo?.phone ||
                        selectedAppointment.userData?.phone ||
                        "Telefon yok"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Yaş</label>
                    <span>
                      {selectedAppointment.userInfo?.age ||
                        selectedAppointment.userData?.age ||
                        "Belirtilmemiş"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Eğitim Durumu</label>
                    <span>
                      {getEducationLabel(
                        selectedAppointment.userInfo?.education ||
                          selectedAppointment.userData?.education
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Tamamlama Detayları</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Teklif Edilen Fiyat</label>
                    <span>
                      €
                      {selectedAppointment.completionDetails?.proposedPrice ||
                        "0"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Anlaşılan Fiyat</label>
                    <span>
                      €
                      {selectedAppointment.completionDetails?.agreedPrice ||
                        "0"}
                    </span>
                  </div>
                  <div className="detail-item full-width">
                    <label>Notlar</label>
                    <div className="notes-content">
                      {selectedAppointment.completionDetails?.notes ||
                        "Not eklenmemiş"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CompletedAppointments;
